import React from 'react';
import { FileText, Image, File, Download, ExternalLink, RefreshCw, ChevronRight, Eye } from 'lucide-react';



//files
/*
id
name
mimeType
size
webViewLink
webContentLink
*/
const FileViewerBien = ({ 
  files = [], 
  isLoading = false, 
  onRefresh, 
  titulo = "Archivos de la tarea",
  esVistaPrevia = false 
}) => {

  // --- Funciones de Utilidad (Se mantienen dentro para limpieza) ---
  const getFileIcon = (mimeType) => {
    if (!mimeType) return <File size={20} className="text-gray-400" />;
    if (mimeType.startsWith('image/')) return <Image size={20} className="text-blue-500" />;
    if (mimeType === 'application/pdf') return <FileText size={20} className="text-red-500" />;
    if (mimeType.includes('word') || mimeType.includes('document')) return <FileText size={20} className="text-blue-600" />;
    if (mimeType.includes('sheet') || mimeType.includes('excel')) return <FileText size={20} className="text-green-600" />;
    return <File size={20} className="text-gray-500" />;
  };

  const formatFileSize = (bytes) => {
    if (!bytes || isNaN(bytes)) return '---';
    const units = ['B', 'KB', 'MB', 'GB'];
    let size = bytes;
    let unitIndex = 0;
    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }
    return `${size.toFixed(1)} ${units[unitIndex]}`;
  };

  const getFileType = (mimeType) => {
    if (!mimeType) return 'Archivo';
    return mimeType.split('/')[1]?.toUpperCase() || 'Doc';
  };

  // --- Estados de Carga y Vacío ---
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-10 bg-slate-50 rounded-3xl border-2 border-dashed border-slate-200">
        <RefreshCw size={32} className="text-blue-500 animate-spin mb-3" />
        <p className="text-slate-500 font-medium">Sincronizando archivos...</p>
      </div>
    );
  }

  if (!files || files.length === 0) {
    return (
      <div className="text-center py-10 bg-slate-50 rounded-3xl border-2 border-dashed border-slate-200">
        <File size={40} className="text-slate-300 mx-auto mb-3" />
        <p className="text-slate-500 font-medium">No hay archivos disponibles</p>
      </div>
    );
  }

  return (
    <div className="w-full animate-in fade-in duration-500">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 px-2">
        <h4 className="text-sm font-black text-slate-800 uppercase tracking-tight">
          {titulo} ({files.length})
        </h4>
        {onRefresh && (
          <button
            onClick={onRefresh}
            className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-full transition-all"
          >
            <RefreshCw size={16} />
          </button>
        )}
      </div>
      
      {/* Lista de Archivos */}
      <div className="grid gap-3">
        {files.map((file, index) => (
          <div 
            key={file.id || index} 
            className={`group flex items-center justify-between p-4 bg-white border rounded-2xl transition-all duration-200 ${
              esVistaPrevia ? 'border-amber-100 hover:border-amber-300 hover:shadow-md' : 'border-slate-100 hover:border-blue-200 hover:shadow-sm'
            }`}
          >
            <div className="flex items-center min-w-0 gap-4">
              <div className={`p-3 rounded-xl shadow-sm ${esVistaPrevia ? 'bg-amber-50' : 'bg-slate-50'}`}>
                {getFileIcon(file.mimeType || file.mimetype)}
              </div>
              
              <div className="text-left overflow-hidden">
                <p className="text-sm font-bold text-slate-700 truncate max-w-[180px] md:max-w-xs">
                  {file.name || file.nombreOriginal}
                </p>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded uppercase ${
                    esVistaPrevia ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-500'
                  }`}>
                    {getFileType(file.mimeType || file.mimetype)}
                  </span>
                  <span className="text-[10px] text-slate-400 font-medium">
                    {formatFileSize(file.size)}
                  </span>
                </div>
              </div>
            </div>

            {/* Acciones */}
            <div className="flex items-center gap-2">
              <a
                href={file.webViewLink || file.url}
                target="_blank"
                rel="noopener noreferrer"
                className={`p-2 rounded-xl transition-colors ${
                  esVistaPrevia 
                  ? 'bg-amber-50 text-amber-600 hover:bg-amber-600 hover:text-white' 
                  : 'bg-blue-50 text-blue-600 hover:bg-blue-600 hover:text-white'
                }`}
                title="Ver archivo"
              >
                {esVistaPrevia ? <Eye size={18} /> : <ExternalLink size={18} />}
              </a>
              
              {!esVistaPrevia && file.webContentLink && (
                <a
                  href={file.webContentLink}
                  download
                  className="p-2 bg-slate-50 text-slate-400 hover:bg-green-600 hover:text-white rounded-xl transition-colors"
                >
                  <Download size={18} />
                </a>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default FileViewerBien;